'use client';

import React from 'react';

const ICONS_BASE = '/payment-icons';

/**
 * Map payment icon key -> path under public/payment-icons.
 * Sources: datatrans/payment-logos (CC-BY-SA-4.0), payrexx/payment-logos (CC0-1.0),
 * simple-icons/simple-icons (CC0-1.0).
 */
const PAYMENT_ICON_PATHS: Record<string, string> = {
  visa: `${ICONS_BASE}/cards/visa.svg`,
  mastercard: `${ICONS_BASE}/cards/mastercard.svg`,
  maestro: `${ICONS_BASE}/cards/maestro.svg`,
  amex: `${ICONS_BASE}/cards/american-express.svg`,
  'american-express': `${ICONS_BASE}/cards/american-express.svg`,
  diners: `${ICONS_BASE}/cards/diners.svg`,
  discover: `${ICONS_BASE}/cards/discover.svg`,
  jcb: `${ICONS_BASE}/cards/jcb.svg`,
  paypal: `${ICONS_BASE}/apm/paypal.svg`,
  skrill: `${ICONS_BASE}/apm/skrill.svg`,
  neteller: `${ICONS_BASE}/payrexx/card_neteller.svg`,
  bitcoin: `${ICONS_BASE}/payrexx/card_bitcoin.svg`,
  ethereum: `${ICONS_BASE}/payrexx/card_ethereum.svg`,
  litecoin: `${ICONS_BASE}/payrexx/card_litecoin.svg`,
  ripple: `${ICONS_BASE}/payrexx/card_ripple.svg`,
  coinbase: `${ICONS_BASE}/payrexx/card_coinbase.svg`,
  binance: `${ICONS_BASE}/simpleicons/binance.svg`,
  ideal: `${ICONS_BASE}/apm/ideal.svg`,
  trustly: `${ICONS_BASE}/payrexx/card_trustly.svg`,
  klarna: `${ICONS_BASE}/apm/klarna.svg`,
  giropay: `${ICONS_BASE}/apm/giropay.svg`,
  sofort: `${ICONS_BASE}/payrexx/card_sofort.svg`,
  paysafecard: `${ICONS_BASE}/apm/paysafecard.svg`,
  applepay: `${ICONS_BASE}/wallets/apple-pay.svg`,
  googlepay: `${ICONS_BASE}/wallets/google-pay.svg`,
  bancontact: `${ICONS_BASE}/apm/bancontact.svg`,
  blik: `${ICONS_BASE}/apm/blik.svg`,
  eps: `${ICONS_BASE}/apm/eps.svg`,
  sepa: `${ICONS_BASE}/apm/sepa.svg`,
  astropay: `${ICONS_BASE}/payrexx/card_astropay.svg`,
  qiwi: `${ICONS_BASE}/payrexx/card_qiwi.svg`,
  revolut: `${ICONS_BASE}/simpleicons/revolut.svg`,
  przelewy24: `${ICONS_BASE}/payrexx/card_przelewy24.svg`,
  rapidtransfer: `${ICONS_BASE}/payrexx/card_rapid_transfer.svg`,
  dankort: `${ICONS_BASE}/payrexx/card_dankort.svg`,
  santander: `${ICONS_BASE}/payrexx/card_santander.svg`,
  bancolombia: `${ICONS_BASE}/payrexx/card_bancolombia.svg`,
  postepay: `${ICONS_BASE}/payrexx/card_postepay.svg`,
  xmoney: `${ICONS_BASE}/payrexx/card_x_money.svg`,
  card: `${ICONS_BASE}/generic/card-generic.svg`,
  banktransfer: `${ICONS_BASE}/generic/card-generic.svg`,
  crypto: `${ICONS_BASE}/apm/crypto.svg`,
};

/** Normalize payment method name to a stable key for icon lookup */
export function getPaymentIconKey(name: string): string {
  const s = String(name ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
  // Aliases: map common variants to canonical key
  const aliases: Record<string, string> = {
    'mastercard': 'mastercard',
    'visa': 'visa',
    'visa-electron': 'visa',
    'american-express': 'amex',
    'amex': 'amex',
    'paypal': 'paypal',
    'skrill': 'skrill',
    'neteller': 'neteller',
    'bitcoin': 'bitcoin',
    'bitcoin-cash': 'bitcoin',
    'ethereum': 'ethereum',
    'apple-pay': 'applepay',
    'google-pay': 'googlepay',
    'bank-transfer': 'banktransfer',
    'ecopayz': 'ecopayz',
    'eco-payz': 'ecopayz',
    'ecopayz-ecocard': 'ecopayz',
    'muchbetter': 'muchbetter',
    'interac': 'interac',
    'interac-online': 'interac',
    'interac-etransfer': 'interac',
    'ideal': 'ideal',
    'trustly': 'trustly',
    'diners-club': 'diners',
    'diners-club-international': 'diners',
    'paysafecard': 'paysafecard',
    'pay-safe-card': 'paysafecard',
    'maestro': 'maestro',
    'litecoin': 'litecoin',
    'tether': 'tether',
    'jeton': 'jeton',
    'jeton-wallet': 'jeton',
    'revolut': 'revolut',
    'klarna': 'klarna',
    'sofort': 'sofort',
    'giropay': 'giropay',
    'neosurf': 'neosurf',
    'astropay': 'astropay',
    'sticpay': 'sticpay',
    'mi-finity': 'mifinity',
    'mifinity': 'mifinity',
    'perfect-money': 'perfectmoney',
    'perfectmoney': 'perfectmoney',
    'monero': 'monero',
    'dogecoin': 'dogecoin',
    'binance': 'binance',
    'credit-card': 'card',
    'card-payouts': 'card',
    'wire-transfer': 'banktransfer',
    'wiretransfer': 'banktransfer',
    'electronic-transfer': 'banktransfer',
    'instant-bank-transfer': 'banktransfer',
    'instant-banking': 'banktransfer',
    'fast-bank-transfer': 'banktransfer',
    'direct-bank-epayments': 'banktransfer',
    'astropay-card': 'astropay',
    'astropay-direct': 'astropay',
    'sofortueberweisung': 'sofort',
    'sofortueberweisung-de': 'sofort',
    'p24': 'przelewy24',
    'przelewy24': 'przelewy24',
    'rapid-transfer': 'rapidtransfer',
    'x-money': 'xmoney',
    '1xmoney': 'xmoney',
    'xmoney': 'xmoney',
    'mercado-pago': 'mercadopago',
    'mercadopago': 'mercadopago',
  };
  return aliases[s] ?? s;
}

const iconSize = 20;

type PaymentMethodIconProps = {
  /** Display name of the payment method (e.g. "Visa", "Bitcoin") */
  name: string;
  /** Show method name next to icon */
  showLabel?: boolean;
  /** Size in pixels for the icon (height; width scales to preserve aspect) */
  size?: number;
  /** Extra class for the wrapper span */
  className?: string;
};

/**
 * Renders a payment method as an icon (if we have one) and optional label.
 * Icons are SVG files from public/payment-icons (datatrans/payment-logos, payrexx/payment-logos).
 * Unknown methods render as label-only in the same pill style.
 */
export function PaymentMethodIcon({
  name,
  showLabel = true,
  size = iconSize,
  className = '',
}: PaymentMethodIconProps) {
  const key = getPaymentIconKey(name);
  const iconPath = key ? PAYMENT_ICON_PATHS[key] : null;
  // Use generic crypto icon for other crypto names if we have no specific icon
  const cryptoFallback = key && !iconPath && /bitcoin|ethereum|crypto|tether|monero|dogecoin|ripple|cardano|litecoin/.test(key)
    ? PAYMENT_ICON_PATHS.crypto
    : null;
  // Use generic card for popular e-wallets without dedicated icons yet
  const cardFallback = key && !iconPath && !cryptoFallback && /ecopayz|muchbetter|interac|neosurf|mifinity|perfectmoney|jeton|mercadopago|advcash/.test(key)
    ? PAYMENT_ICON_PATHS.card
    : null;
  const src = iconPath ?? cryptoFallback ?? cardFallback;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded border border-white/10 bg-[#0B0D19] px-2.5 py-1 text-xs text-gray-400 ${className}`}
      title={name}
    >
      {src ? (
        <img
          src={src}
          alt={name}
          width={Math.round(size * 1.5)}
          height={size}
          className="shrink-0 object-contain"
          style={{ height: size, width: 'auto' }}
        />
      ) : null}
      {showLabel ? <span className={src ? '' : 'text-gray-400'}>{name}</span> : null}
    </span>
  );
}

type PaymentMethodIconsProps = {
  methods: string[];
  showLabel?: boolean;
  max?: number;
  className?: string;
};

/**
 * Renders a list of payment method pills with icons.
 */
export function PaymentMethodIcons({
  methods,
  showLabel = true,
  max,
  className = '',
}: PaymentMethodIconsProps) {
  const list = Array.isArray(methods) ? methods.filter(Boolean) : [];
  const toShow = max != null ? list.slice(0, max) : list;

  if (toShow.length === 0) {
    return (
      <span className={`rounded border border-white/10 bg-[#0B0D19] px-3 py-1 text-xs text-gray-500 ${className}`}>
        —
      </span>
    );
  }

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {toShow.map((method) => (
        <PaymentMethodIcon key={method} name={method} showLabel={showLabel} />
      ))}
    </div>
  );
}
